import { Routes } from '@angular/router';
import { authGuard, featureGuard, roleGuard, tenantGuard } from './core/guards/auth.guard';
import { pageTransitionGuard } from './core/guards/page-transition.guard';
import { UnauthorizedComponent } from './main/unauthorized/unauthorized.component';
import { NotFoundPage } from './main/not-found/not-found.page';
import { CallbackPage } from './main/callback/callback.page';
import { InvitePage } from './main/invite/invite.page';
import { OnboardingPage } from './main/onboarding/onboarding.page';
import { NoTenantPage } from './main/no-tenant/no-tenant.page';
import { ContactPage } from './main/contact/contact.page';
import { CardPage } from './main/card/card.page';
import { ProductsPage } from './main/products/products.page';
import { PricingPage } from './main/pricing/pricing.page';
import { PrivacyPage } from './main/privacy/privacy.page';
import { TermsPage } from './main/terms/terms.page';
import { StatusPage } from './main/status/status.page';
import { SecurityPage } from './main/security/security.page';
import { DeveloperPage } from './main/developer/developer.page';

export const routes: Routes = [
  // Default redirect. card.gastro-management.ch is a plain hostname alias for this same
  // app (one more custom domain on the PROD-Portal-SPA App Service, no separate deploy) —
  // its root path shows the digital business card instead of the marketing home page.
  // No SSR here (see src/server.js), so `window` is always available.
  { path: '', redirectTo: () => (window.location.hostname === 'card.gastro-management.ch' ? 'card' : 'home'), pathMatch: 'full' },

  // Auth0 callback
  { path: 'callback', component: CallbackPage },

  // Auth0 Organization invitation landing (Application Login URI target) — public by
  // definition: the invitee has no account yet when they arrive here.
  { path: 'invite', component: InvitePage },

  // Public pages
  { path: 'unauthorized', component: UnauthorizedComponent },
  { path: 'not-found', component: NotFoundPage },
  { path: 'onboarding', component: OnboardingPage },
  { path: 'no-tenant', component: NoTenantPage, canActivate: [authGuard] },
  { path: 'products', component: ProductsPage, canActivate: [pageTransitionGuard] },
  { path: 'pricing', component: PricingPage, canActivate: [pageTransitionGuard] },
  { path: 'tablestack', loadChildren: () => import('./pages/tablestack/tablestack.routes').then(m => m.TABLESTACK_ROUTES), canActivate: [authGuard, tenantGuard, featureGuard('TableStack')] },
  { path: 'contact', component: ContactPage, canActivate: [pageTransitionGuard] },
  { path: 'card', component: CardPage, canActivate: [pageTransitionGuard] },
  { path: 'privacy', component: PrivacyPage, canActivate: [pageTransitionGuard] },
  { path: 'terms', component: TermsPage, canActivate: [pageTransitionGuard] },
  { path: 'status', component: StatusPage, canActivate: [pageTransitionGuard] },
  { path: 'security', component: SecurityPage, canActivate: [pageTransitionGuard] },
  { path: 'sicherheit', redirectTo: 'security', pathMatch: 'full' },
  { path: 'developer', component: DeveloperPage, canActivate: [pageTransitionGuard] },

  // German-path aliases → canonical English routes. Angular preserves query params and
  // the URL fragment on redirectTo, so e.g. /datenschutz?lang=de#menu → /privacy?lang=de#menu.
  { path: 'datenschutz', redirectTo: 'privacy', pathMatch: 'full' },
  { path: 'agb', redirectTo: 'terms', pathMatch: 'full' },
  { path: 'impressum', redirectTo: 'imprint', pathMatch: 'full' },

  // Lazy-loaded public pages
  {
    path: 'imprint',
    loadChildren: () => import('./main/imprint/imprint.routes').then(m => m.IMPRINT_ROUTES),
    canActivate: [pageTransitionGuard],
  },

  // Home: public landing + authenticated dashboard
  {
    path: 'home',
    loadChildren: () => import('./main/home/home.routes').then(m => m.HOME_ROUTES),
    canActivate: [pageTransitionGuard],
  },

  // Portal: tenant
  {
    path: 'company',
    loadChildren: () => import('./pages/company/company.routes').then(m => m.COMPANY_ROUTES),
    canActivate: [authGuard, tenantGuard, featureGuard('Unternehmen')],
    data: { preload: false }, // chunk carries echarts: fetch on first navigation, not for every user
  },

  // Portal: users (portal access / invite flow)
  { path: 'users', loadChildren: () => import('./pages/users/users.routes').then(m => m.USERS_ROUTES), canActivate: [authGuard, tenantGuard, featureGuard('Mitarbeiter')] },

  // Portal: employees (plain HR master data, independent of portal access)
  { path: 'employees', loadChildren: () => import('./pages/employees/employees.routes').then(m => m.EMPLOYEES_ROUTES), canActivate: [authGuard, tenantGuard, featureGuard('Personaldaten')] },

  // Portal: departments + positions
  { path: 'departments', loadChildren: () => import('./pages/departments/departments.routes').then(m => m.DEPARTMENTS_ROUTES), canActivate: [authGuard, tenantGuard, featureGuard('Abteilungen')] },

  // Portal: user access / role management
  { path: 'access', loadChildren: () => import('./pages/access/access.routes').then(m => m.ACCESS_ROUTES), canActivate: [authGuard, tenantGuard, featureGuard('Zugriff')] },

  // Portal: Speisekarte / Menu
  { path: 'menus', loadChildren: () => import('./pages/menus/menus.routes').then(m => m.MENUS_ROUTES), canActivate: [authGuard, tenantGuard, featureGuard('Speisekarte')] },

  // Settings (display preferences, security, account)
  { path: 'settings', loadChildren: () => import('./pages/settings/settings.routes').then(m => m.SETTINGS_ROUTES), canActivate: [authGuard] },

  // Portal: documents (contracts/files, tenant + admin)
  { path: 'documents', loadChildren: () => import('./pages/documents/documents.routes').then(m => m.DOCUMENTS_ROUTES), canActivate: [authGuard, tenantGuard, featureGuard('Dokumente')] },

  // Portal: invoices, tenant self-service ('invoices') and platform-admin management ('admin/invoices')
  { path: 'invoices', loadChildren: () => import('./pages/invoice-management/invoice-management.routes').then(m => m.INVOICE_MANAGEMENT_ROUTES), canActivate: [authGuard, tenantGuard, featureGuard('Rechnungen')] },

  { path: 'admin/invoices', loadChildren: () => import('./pages/invoices/invoices.routes').then(m => m.INVOICES_ROUTES), canActivate: [authGuard, tenantGuard] },

  // Back-compat: old tenant route → new
  { path: 'invoice-management', redirectTo: 'invoices', pathMatch: 'full' },

  // Portal: gateways (lazy-loaded feature module, heaviest cluster: VNC/VPN/modules)
  {
    path: 'devices',
    loadChildren: () => import('./pages/devices/devices.routes').then(m => m.DEVICES_ROUTES),
    canActivate: [authGuard, tenantGuard, featureGuard('Geraete')],
    data: { preload: false }, // heaviest cluster (VNC/VPN): fetch on first navigation, not eagerly
  },

  // Portal: support tickets. Deliberately NOT featureGuard-gated: SupportFunctions.cs lets any
  // authenticated tenant member open/read/reply to their OWN ticket regardless of their Support
  // permission (only viewing/managing OTHER members' tickets requires Support:Read/Write) — the
  // route must stay reachable by everyone so that "contact support" always works.
  { path: 'support', loadChildren: () => import('./pages/support/support.routes').then(m => m.SUPPORT_ROUTES), canActivate: [authGuard, tenantGuard] },

  // Admin only (lazy-loaded feature module: overview, konfiguration, anfragen)
  {
    path: 'admin',
    loadChildren: () => import('./pages/admin/admin.routes').then(m => m.ADMIN_ROUTES),
    canActivate: [authGuard, roleGuard('Admin')],
    data: { preload: false }, // platform-admin only: not worth preloading for every user
  },

  // ScreenDeck settings (lazy)
  { path: 'screendeck/settings', loadChildren: () => import('./pages/screendeck/screendeck-settings.routes').then(m => m.SCREENDECK_SETTINGS_ROUTES), canActivate: [authGuard, tenantGuard, featureGuard('ScreenDeck')] },

  // Chroniq (lazy): informational landing page, product itself lives in its own mobile app
  { path: 'chroniq', loadChildren: () => import('./pages/chroniq/chroniq.routes').then(m => m.CHRONIQ_ROUTES), canActivate: [authGuard, tenantGuard] },

  // Catch-all
  { path: '**', redirectTo: 'home' },
];
